import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker, Tag } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Chip } from "@/components/StepBits";
import { Field, TextArea, SubmitButton } from "@/components/FormBits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOMES = [
  ["Stronger Engagement", "Support that children, young people and families are more willing and able to engage with — because relationship, choice and what matters to them are central."],
  ["Less System Overwhelm", "Reducing unnecessary duplication, repeated storytelling and disconnected service experiences where partnership design allows."],
  ["Relational Continuity", "Greater consistency around children, young people and families — rather than continually rebuilding trust with new people."],
  ["Education Connection", "Supporting engagement, participation, alternative pathways and connection to learning where education forms part of the person's hopes and goals."],
  ["Family & Placement Stability", "Strengthening relationships and the supports around children, families and carers where appropriate. We do not promise prevention of placement breakdown."],
  ["Identity, Culture & Belonging", "Creating space for identity and belonging while supporting pathways toward the person's own family, community, Nation and appropriate cultural authorities."],
  ["Confidence & Capacity", "Building skills, knowledge, confidence, decision-making and possibilities that exist beyond the professional relationship."],
  ["Stronger Community Around the Person", "Building pathways toward family, peers, community, cultural connection and informal supports — rather than unnecessary long-term service dependence."],
  ["Better Connected Systems", "Supporting the people and services already involved to work alongside the person's goals, relationships, lived context and existing knowledge."],
];

const PARTNERSHIPS = [
  {
    t: "Fund a Pilot",
    d: "Help take a developing Ngawilyi idea into real-world delivery. Pilot funding may support the development, delivery and evaluation of emerging programs, community initiatives or practice approaches.",
    bullets: ["What worked?", "What didn't?", "What did people experience?", "What changed?", "What should happen next?"],
    note: "A pilot partnership should include meaningful learning and evaluation so together we can understand:",
    cta: "Discuss a Pilot",
    interest: "Funding a pilot",
  },
  {
    t: "Community Consultation",
    d: "Work with Ngawilyi to create relational and culturally responsive ways of hearing from children, young people, families, carers and community. Consultation should create genuine opportunities for people to influence what happens next — not simply collect feedback after decisions have already been made.",
    cta: "Discuss Community Consultation",
    interest: "Community consultation",
  },
  {
    t: "ACCO Support & Collaboration",
    d: "Partner with Aboriginal Community Controlled Organisations where Ngawilyi can add capacity, relational support or a complementary practice approach. Ngawilyi does not seek to duplicate or replace the cultural authority, community relationships, knowledge or services already held by ACCOs. Partnership should strengthen Aboriginal community-controlled responses wherever possible.",
    cta: "Yarn About ACCO Collaboration",
    interest: "ACCO collaboration",
  },
  {
    t: "Education Partnership",
    d: "Partner with schools and education settings around children and young people experiencing disengagement, repeated suspension, relationship difficulties or barriers to participation.",
    bullets: ["Relational engagement", "Mentoring", "Narrative Practice", "Education connection", "Alternative engagement", "Family/carer connection", "Community-based activities", "Transition support", "Consultation", "Pilot programs"],
    note: "Potential partnership approaches may include:",
    cta: "Discuss an Education Partnership",
    interest: "Education partnership",
  },
  {
    t: "Government Partnership",
    d: "Work with Ngawilyi on pilots, commissioned services, consultation, program delivery or practice innovation across child and family support, child protection, youth justice, education, Aboriginal services, community development and early intervention. Partnerships should preserve Ngawilyi's relational and community-led practice — rather than requiring the work to become another rigid service pathway.",
    cta: "Start a Government Partnership Conversation",
    interest: "Government partnership",
  },
  {
    t: "NGO Referral Partnership",
    d: "Create a clear pathway for NGOs and community services to refer people into appropriate Ngawilyi supports or programs.",
    bullets: ["Who Ngawilyi may support", "Available programs", "Referral pathways", "Funding arrangements", "Communication", "Consent", "Roles and responsibilities", "How Ngawilyi works alongside existing services"],
    note: "Referral partnerships may help clarify:",
    cta: "Discuss a Referral Partnership",
    interest: "NGO referral partnership",
  },
  {
    t: "Philanthropic Partnership",
    d: "Support work that may not fit neatly within existing service funding models. Philanthropic investment may help Ngawilyi pilot emerging ideas, build community spaces, increase accessibility, develop resources, support community-led initiatives, evaluate new approaches and create opportunities that conventional service funding does not easily support. The focus remains on sustainable community benefit — not funding activity for activity's sake.",
    cta: "Discuss Philanthropic Partnership",
    interest: "Philanthropic partnership",
  },
  {
    t: "Research & Evaluation Partnership",
    d: "Partner with Ngawilyi to strengthen evidence around relational, Narrative Practice, community-building and culturally responsive approaches — with universities, researchers, evaluators, Aboriginal researchers, community-controlled research organisations and practice organisations. Research and evaluation involving Aboriginal people and communities must respect appropriate ethics, governance, cultural authority and community ownership of knowledge. Evaluation should be shaped with people and community — not simply done to them.",
    chips: ["Data", "Stories", "Individual outcomes", "Community voice", "Practice learning"],
    cta: "Discuss Research & Evaluation",
    interest: "Research & evaluation",
  },
  {
    t: "Practice & Resource Collaboration",
    d: "Work together on tools, resources, workforce learning, Narrative Practice approaches, community resources or other ideas that could strengthen practice beyond a single service — with practitioners, Aboriginal organisations, community organisations, lived-experience knowledge holders, specialist services, educators and others bringing useful knowledge. The purpose is not for Ngawilyi to hold all the knowledge. It is to create opportunities for useful knowledge to travel.",
    cta: "Bring Us an Idea",
    interest: "Practice/resource collaboration",
  },
];

const STEPS = [
  ["Yarn", "Tell us what you're noticing, exploring or trying to change."],
  ["Find the shared purpose", "What matters to the people involved? What is already working? Where is the gap?"],
  ["Design together", "Agree on the role Ngawilyi could play, the boundaries of the partnership and what everyone hopes it could make possible."],
  ["Pilot / Partner / Refer", "Put the idea into practice at an appropriate scale."],
  ["Listen & learn", "Gather data, stories, community feedback and practice learning."],
  ["Adapt", "Use what we learn to decide what should continue, change, grow or stop."],
];

const INTERESTS = ["Funding a pilot", "Community consultation", "ACCO collaboration", "Education partnership", "Government partnership", "NGO referral partnership", "Philanthropic partnership", "Research & evaluation", "Practice/resource collaboration", "Something else", "Not sure yet"];

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const PartnerForm = ({ preset }) => {
  const [form, setForm] = useState({ name: "", organisation: "", role: "", email: "", phone: "", interests: preset || [], explore: "", preferred_contact: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (done) {
    return (
      <div data-testid="partner-success" className="rounded-[48px] bg-creamdark border border-ochre/25 p-12 sm:p-16 text-center">
        <CheckCircle2 className="h-12 w-12 text-terracotta mx-auto mb-6" />
        <h3 className="font-display font-light text-4xl text-charcoal">Thanks for reaching out.</h3>
        <p className="mt-5 text-earth/85 leading-relaxed max-w-xl mx-auto">
          We've received your partnership enquiry. A member of the Ngawilyi team will be in touch to continue the conversation.
        </p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Please add your name and email.");
    setBusy(true);
    try {
      await axios.post(`${API}/partnerships`, form);
      setDone(true);
      window.scrollTo({ top: document.getElementById("partner-form").offsetTop - 200, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong sending that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="partner-form-el" onSubmit={submit} className="rounded-[48px] bg-creamdark border border-ochre/25 p-8 sm:p-14">
      <div className="grid sm:grid-cols-2 gap-6">
        <Field label="Name" testid="pf-name-input" value={form.name} onChange={set("name")} required />
        <Field label="Organisation" testid="pf-org-input" optional value={form.organisation} onChange={set("organisation")} />
        <Field label="Role / position" testid="pf-role-input" optional value={form.role} onChange={set("role")} />
        <Field label="Email" testid="pf-email-input" type="email" value={form.email} onChange={set("email")} required />
        <Field label="Phone" testid="pf-phone-input" optional value={form.phone} onChange={set("phone")} />
        <div>
          <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-earth">Preferred contact</span>
          <div className="flex gap-3 pt-1">
            {["Email", "Phone"].map((t) => (
              <Chip key={t} testid={`pf-contact-${t.toLowerCase()}`} selected={form.preferred_contact === t} onClick={() => setForm((f) => ({ ...f, preferred_contact: f.preferred_contact === t ? "" : t }))}>{t}</Chip>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-8">
        <span className="mb-3 block text-xs font-bold uppercase tracking-[0.18em] text-earth">I'm interested in</span>
        <div className="flex flex-wrap gap-3">
          {INTERESTS.map((t) => (
            <Chip key={t} testid={`pf-interest-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} selected={form.interests.includes(t)} onClick={() => setForm((f) => ({ ...f, interests: toggle(f.interests, t) }))}>{t}</Chip>
          ))}
        </div>
      </div>
      <div className="mt-8">
        <TextArea label="What would you like to explore?" testid="pf-explore-textarea" optional rows={5} value={form.explore} onChange={set("explore")} />
        <p className="mt-3 text-sm text-earth/70">You don't need a finished proposal. Tell us what you're thinking about, what you're noticing or what you're hoping a partnership could make possible.</p>
      </div>
      <div className="mt-8">
        <SubmitButton testid="pf-submit-btn" busy={busy}>Start the Conversation</SubmitButton>
      </div>
    </form>
  );
};

const Partner = () => {
  const [formKey, setFormKey] = useState(0);
  const [preset, setPreset] = useState([]);

  const discuss = (interest) => {
    setPreset([interest]);
    setFormKey((k) => k + 1);
    document.getElementById("partner-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="partner-page">
      <PageHero
        kicker="Partner With Us"
        title={"One relationship. Multiple life domains.\nLess system overwhelm."}
        lede="Children, young people and families can find themselves surrounded by services — each responsible for one piece of life. Education over here. Therapy over there. Placement support somewhere else. Community connection somewhere else again. More services do not automatically create more support — sometimes they create more appointments, more handovers, more people to trust, more stories to retell and more systems to navigate. Ngawilyi explores a different possibility: a consistent relational connection that can move alongside the person across everyday life, while working with the specialist, cultural, community and statutory supports already around them."
      >
        <div className="mt-10 flex flex-wrap gap-4">
          <a data-testid="partner-hero-start-btn" href="#partner-form" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
            Start a Partnership Conversation <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <Link data-testid="partner-hero-approach-btn" to="/approach" className="inline-flex items-center gap-2 rounded-full border-2 border-charcoal px-8 py-[14px] text-sm font-bold uppercase tracking-widest text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-cream">
            Explore Our Approach
          </Link>
        </div>
      </PageHero>

      <Marquee items={["Partner.", "Refer.", "Consult.", "Co-design.", "Fund.", "Evaluate.", "Connect."]} />

      {/* THE PARTNERSHIP MODEL */}
      <section className="px-6 py-16 sm:py-24 bg-cream">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <Kicker>Why partner?</Kicker>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
              Less fragmentation. <span className="italic text-terracotta">More relationship.</span>
            </h2>
            <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
              Ngawilyi is not trying to replace every specialist around a person. We are interested in what becomes
              possible when support connects rather than fragments. These parts of life do not exist separately
              just because services are funded separately — Ngawilyi can work relationally across multiple life
              domains within the scope of the support being provided.
            </p>
            <p className="mt-5 text-base lg:text-lg leading-relaxed text-earth">
              Where specialist expertise, statutory responsibility or cultural authority sits elsewhere, we work
              alongside it rather than attempting to replace it.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-[40px] bg-creamdark border border-ochre/25 p-10 sm:p-12">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-6">A child, young person or family may be navigating</p>
              <div className="flex flex-wrap gap-2.5">
                {["School", "Family", "Placement", "Identity", "Culture", "Community", "Relationships", "Confidence", "Everyday skills", "Systems"].map((t) => (
                  <span key={t} className="rounded-full bg-cream border border-ochre/40 px-4 py-2 text-[13px] font-semibold text-earth">{t}</span>
                ))}
              </div>
              <p className="mt-8 font-display italic text-2xl sm:text-3xl leading-snug text-charcoal">
                "One relationship can hold connection across multiple parts of life — without pretending to hold every role."
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LESS SYSTEM OVERWHELM */}
      <section className="px-6 py-16 sm:py-24 bg-charcoal text-cream grain relative overflow-hidden">
        <div className="absolute -top-24 left-1/4 w-96 h-96 bg-terracotta/25 organic-blob blur-3xl" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08]">
              Support shouldn't become <span className="italic text-ochre">another system to manage.</span>
            </h2>
            <p className="mt-8 text-cream/80 leading-relaxed">
              Partnership with Ngawilyi looks for opportunities to reduce unnecessary fragmentation while
              strengthening communication and relationships between the supports already around the person.
            </p>
            <p className="mt-8 font-display italic text-2xl sm:text-3xl leading-snug text-ochre">
              "Not more services for the sake of more services. Better connection around the person."
            </p>
          </Reveal>
          <div className="space-y-3">
            {["Another appointment.", "Another assessment.", "Another referral.", "Another professional.", "Another story to retell.", "Another relationship to build."].map((s, i) => (
              <Reveal key={s} delay={i * 0.06}>
                <div className="rounded-3xl bg-cream/[0.06] border border-cream/12 px-7 py-5 font-display text-2xl font-light text-cream/90">{s}</div>
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <p className="pt-3 text-sm text-cream/60">For some children, young people and families, getting support can create another layer of work.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INTENDED OUTCOMES */}
      <section className="px-6 py-16 sm:py-24 bg-cream">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-4">
            <Kicker>What are we trying to make more possible?</Kicker>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
              Partnership should create <span className="italic text-terracotta">something useful in real life.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mb-10"><Tag tone="ochre">Intended outcomes — to be tested through pilot and evaluation, not yet proven</Tag></div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {OUTCOMES.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div data-testid={`outcome-card-${i}`} className="rounded-[32px] bg-creamdark border border-ochre/25 p-8 h-full transition-transform duration-300 hover:-translate-y-1">
                  <span className="font-display italic text-terracotta/60 text-lg">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-2 font-semibold text-charcoal uppercase tracking-wide text-sm">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-earth/85">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-10 font-display italic text-2xl sm:text-3xl text-charcoal max-w-3xl">
              "The intended outcome isn't dependence on Ngawilyi. <span className="text-terracotta">It is greater strength around the person.</span>"
            </p>
          </Reveal>
        </div>
      </section>

      {/* NINE WAYS TO PARTNER */}
      <section className="px-6 py-16 sm:py-24 bg-creamdark grain relative overflow-hidden">
        <div className="absolute -bottom-28 -right-24 w-96 h-96 bg-terracotta/10 organic-blob blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <Reveal className="mb-12">
            <Kicker>Work with Ngawilyi</Kicker>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
              Nine ways to be part of <span className="italic text-terracotta">what we're building.</span>
            </h2>
            <p className="mt-6 text-earth/85 leading-relaxed max-w-2xl">
              Partnership does not need to begin with a finished proposal. Start with what you're noticing, what
              you're trying to make possible or where you think something different could help.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PARTNERSHIPS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.05}>
                <div data-testid={`partnership-card-${i}`} className={`rounded-[36px] p-9 h-full flex flex-col transition-transform duration-500 hover:-translate-y-2 ${i === 0 ? "bg-terracotta text-cream" : "bg-cream border border-ochre/25"}`}>
                  <span className={`font-display italic text-lg ${i === 0 ? "text-cream/60" : "text-terracotta/50"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <h3 className={`mt-3 font-display text-3xl font-light leading-snug ${i === 0 ? "" : "text-charcoal"}`}>{p.t}</h3>
                  <p className={`mt-3 text-sm leading-relaxed flex-1 ${i === 0 ? "text-cream/85" : "text-earth/85"}`}>{p.d}</p>
                  {p.note && <p className={`mt-4 text-xs font-bold uppercase tracking-wider ${i === 0 ? "text-cream/70" : "text-earth/60"}`}>{p.note}</p>}
                  {p.bullets && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.bullets.map((b) => (
                        <span key={b} className={`rounded-full px-3 py-1.5 text-[11px] font-semibold ${i === 0 ? "bg-cream/12 border border-cream/25 text-cream/90" : "bg-creamdark border border-ochre/40 text-earth"}`}>{b}</span>
                      ))}
                    </div>
                  )}
                  {p.chips && (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {p.chips.map((c, ci) => (
                        <span key={c} className="flex items-center gap-2">
                          <span className="rounded-full bg-blush/40 border border-blush px-3 py-1.5 text-[11px] font-semibold text-earth">{c}</span>
                          {ci < p.chips.length - 1 && <span className="text-terracotta font-bold">+</span>}
                        </span>
                      ))}
                      <Link to="/impact" data-testid="partnership-impact-link" className="text-[11px] font-bold uppercase tracking-widest text-terracotta underline underline-offset-4">See our Impact approach</Link>
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid={`partnership-cta-${i}`}
                    onClick={() => discuss(p.interest)}
                    className={`group mt-7 inline-flex w-fit items-center gap-2 rounded-full px-6 py-3.5 text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:scale-105 ${i === 0 ? "bg-cream text-terracotta" : "bg-terracotta text-cream"}`}
                  >
                    {p.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP WITHOUT REPLACEMENT */}
      <section className="px-6 py-16 sm:py-24 bg-charcoal text-cream grain relative overflow-hidden">
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-terracotta/25 organic-blob-alt blur-3xl" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08]">
              Working together doesn't mean <span className="italic text-ochre">everybody does the same job.</span>
            </h2>
            <p className="mt-8 text-cream/80 leading-relaxed">
              Ngawilyi respects the different responsibilities and knowledge held across families, community,
              Elders, cultural authorities, ACCOs, schools, statutory services, clinicians, allied health
              professionals, community organisations, researchers and other specialist services. Partnership does
              not mean Ngawilyi takes over those roles.
            </p>
            <p className="mt-8 font-display italic text-2xl sm:text-3xl leading-snug text-ochre">
              "Collaboration should reduce fragmentation — not create another layer of it."
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-[40px] bg-cream/[0.06] border border-cream/12 p-10 sm:p-12 backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-ochre mb-6">Instead, we can ask together</p>
              <ul className="space-y-4">
                {["Who already holds knowledge here?", "Who already has the relationship?", "What is already working?", "What is missing?", "What is being duplicated?", "Where is the person experiencing gaps between services?", "What could become easier if we worked differently together?"].map((q) => (
                  <li key={q} className="flex gap-4 items-start text-cream/85 leading-relaxed">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" />
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW PARTNERSHIP CAN START */}
      <section className="px-6 py-16 sm:py-24 bg-cream">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>Start small or think big</Kicker>
              <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
                A partnership can begin <span className="italic text-terracotta">with a yarn.</span>
              </h2>
              <Link data-testid="partner-impact-approach-btn" to="/impact" className="group mt-8 inline-flex items-center gap-2 rounded-full border-2 border-charcoal px-7 py-3.5 text-xs font-bold uppercase tracking-widest text-charcoal transition-colors hover:bg-charcoal hover:text-cream">
                Explore Our Impact Approach <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            {STEPS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="flex items-start gap-5">
                  <span className="shrink-0 h-11 w-11 rounded-full bg-terracotta/12 border border-terracotta/35 flex items-center justify-center font-display italic text-terracotta text-lg">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="py-2">
                    <p className="font-display text-2xl text-charcoal">{t}</p>
                    <p className="mt-1 text-sm text-earth/80 leading-relaxed">{d}</p>
                  </div>
                </div>
                {i < STEPS.length - 1 && <div className="ml-[22px] h-5 w-px bg-gradient-to-b from-terracotta/40 to-transparent" />}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOMETHING ELSE IN MIND */}
      <section className="px-6 pb-16 sm:pb-24 bg-cream">
        <Reveal>
          <div className="max-w-7xl mx-auto rounded-[48px] bg-blush/40 p-10 sm:p-16 grain relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-terracotta/15 organic-blob blur-2xl" />
            <div className="relative max-w-3xl">
              <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
                Not every good partnership <span className="italic text-terracotta">fits one of nine boxes.</span>
              </h2>
              <p className="mt-6 text-earth/85 leading-relaxed">
                If you can see a place where Ngawilyi's approach could add something useful, bring the idea to us.
                We would rather start with a conversation about what you're trying to make possible than make you
                squeeze the idea into a partnership category.
              </p>
              <button type="button" data-testid="partner-idea-btn" onClick={() => discuss("Something else")} className="group mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
                Bring Us an Idea <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* PARTNERSHIP FORM */}
      <section id="partner-form" className="px-6 py-16 sm:py-24 bg-cream scroll-mt-24">
        <div className="max-w-4xl mx-auto">
          <Reveal className="mb-10">
            <Kicker>Partner With Us</Kicker>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">Start the conversation.</h2>
            <p className="mt-5 text-earth/85 leading-relaxed max-w-2xl">
              You don't need a finished proposal. Tell us what you're thinking about, what you're noticing or what
              you're hoping a partnership could make possible.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <PartnerForm key={formKey} preset={preset} />
          </Reveal>
        </div>
      </section>

      {/* CROSS-LINKS */}
      <section className="px-6 pb-16 sm:pb-24 bg-cream">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-6">Keep exploring</p>
            <div className="flex flex-wrap gap-3">
              {[["Our Approach — the Ngawilyi model", "/approach"], ["Programs — current & proposed", "/programs"], ["Make a Referral", "/referrals"], ["Our Impact — how we evaluate & share learning", "/impact"], ["Help Shape Ngawilyi — contribute ideas", "/consultation"], ["Contact — general enquiries", "/contact"]].map(([l, to]) => (
                <Link key={to} data-testid={`partner-xlink-${to.slice(1)}`} to={to} className="rounded-full bg-creamdark border border-ochre/30 px-5 py-2.5 text-sm font-semibold text-earth transition-all duration-300 hover:border-terracotta hover:text-terracotta">
                  {l}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL */}
      <section className="px-6 py-20 sm:py-28 bg-cream">
        <Reveal>
          <div className="relative overflow-hidden max-w-6xl mx-auto rounded-[48px] bg-charcoal text-cream px-8 py-16 sm:p-20 grain">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-terracotta/40 organic-blob blur-3xl" />
            <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-ochre/25 organic-blob-alt blur-3xl" />
            <div className="relative">
              <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
                Different roles. Shared purpose. <span className="italic text-ochre">Better connected support.</span>
              </h2>
              <p className="mt-6 text-cream/80 max-w-2xl leading-relaxed">
                Ngawilyi doesn't need to hold every piece of somebody's life. But we believe there is value in
                having relationships that help those pieces connect. If your organisation is trying to create
                stronger engagement, reduce fragmentation, build community connection or test a different way of
                working alongside children, young people and families — we'd like to hear what you're thinking.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a data-testid="partner-final-start-btn" href="#partner-form" className="rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
                  Start a Partnership Conversation
                </a>
                <Link data-testid="partner-final-impact-btn" to="/impact" className="rounded-full border border-cream/40 px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-cream/10">
                  Explore Our Impact
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Partner;
